import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import User from "@mapmycustomers/shared/types/User";

import { EntityMention } from "@app/component/EntityMentions";
import CoachingFile from "@app/scene/dashboard/types/CoachingFile";
import CoachingNote, {
  CoachingNoteAnswer,
  CoachingNoteAttachmentChip,
  CoachingNoteVisibility,
} from "@app/scene/dashboard/types/CoachingNote";
import PendingShareEntity from "@app/scene/dashboard/types/PendingShareEntity";

export const fetchCoachingNotes = createAsyncAction(
  "@dashboard/coaching/notes/fetch/request",
  "@dashboard/coaching/notes/fetch/success",
  "@dashboard/coaching/notes/fetch/failure"
)<
  {
    append?: boolean;
    categoryId?: null | number;
    limit?: number;
    offset?: number;
    parentNoteId?: CoachingNote["id"] | null;
    q?: string;
    // Background refetch — reducer keeps existing notes + loading state so
    // the panel doesn't flash skeletons while a poll-driven refresh resolves.
    silent?: boolean;
    subjectUserId: User["id"];
    visibility?: CoachingNoteVisibility;
  },
  { append: boolean; hasMore: boolean; notes: CoachingNote[]; offset: number; silent: boolean },
  unknown
>();

export const createCoachingNote = createAsyncAction(
  "@dashboard/coaching/notes/create/request",
  "@dashboard/coaching/notes/create/success",
  "@dashboard/coaching/notes/create/failure"
)<
  {
    answers: CoachingNoteAnswer[];
    // Pre-shaped attachment chips so the reducer can seed the optimistic temp
    // note's `attachments[]` without re-resolving against the pool selector —
    // matches the denormalized chip shape the BE returns on list/fetch.
    attachments?: CoachingNoteAttachmentChip[];
    categoryId?: null | number;
    coachingFileIds?: Array<CoachingFile["id"]>;
    mentions: EntityMention[];
    // Files dragged into the compose form that aren't yet in the rep's pool.
    // The saga uploads them via createCoachingFile, folds the resulting
    // CoachingFile ids into coachingFileIds, and dispatches the standard
    // pool-update via uploadCoachingFiles.success. Without this, dropped-in
    // files only ended up in the pool and the new note showed no chip.
    newFiles?: Array<Blob | File>;
    note: string;
    subjectUserId: User["id"];
    tempAuthor: Pick<User, "fullName" | "id" | "username">;
    tempCreatedAt: string;
    tempId: CoachingNote["id"];
    visibility: CoachingNoteVisibility;
  },
  { note: CoachingNote; tempId: CoachingNote["id"] },
  { tempId: CoachingNote["id"] }
>();

// Request payload widens the note with `coachingFileIds` so the inline-edit
// picker can re-link/unlink pool files, and with `newFiles` so dropped-in
// files can be uploaded atomically with the update. Detach-on-remove is
// handled BE-side by _syncAttachments — the FE doesn't need to surface
// which files were removed.
export const updateCoachingNote = createAsyncAction(
  "@dashboard/coaching/notes/update/request",
  "@dashboard/coaching/notes/update/success",
  "@dashboard/coaching/notes/update/failure"
)<
  CoachingNote & {
    coachingFileIds?: Array<CoachingFile["id"]>;
    newFiles?: Array<Blob | File>;
  },
  CoachingNote,
  CoachingNote["id"]
>();

export const deleteCoachingNote = createAsyncAction(
  "@dashboard/coaching/notes/delete/request",
  "@dashboard/coaching/notes/delete/success",
  "@dashboard/coaching/notes/delete/failure"
)<
  { noteId: CoachingNote["id"]; parentNoteId?: CoachingNote["id"] | null },
  { noteId: CoachingNote["id"]; parentNoteId?: CoachingNote["id"] | null },
  CoachingNote["id"]
>();

export const fetchReplies = createAsyncAction(
  "@dashboard/coaching/notes/fetchReplies/request",
  "@dashboard/coaching/notes/fetchReplies/success",
  "@dashboard/coaching/notes/fetchReplies/failure"
)<
  {
    append?: boolean;
    limit?: number;
    offset?: number;
    parentNoteId: CoachingNote["id"];
    silent?: boolean;
  },
  {
    append: boolean;
    hasMore: boolean;
    offset: number;
    parentNoteId: CoachingNote["id"];
    replies: CoachingNote[];
    silent: boolean;
  },
  { error: unknown; parentNoteId: CoachingNote["id"] }
>();

export const createReply = createAsyncAction(
  "@dashboard/coaching/notes/createReply/request",
  "@dashboard/coaching/notes/createReply/success",
  "@dashboard/coaching/notes/createReply/failure"
)<
  {
    // Same purpose, and same denormalized chip shape, as
    // `createCoachingNote.attachments` — lets the reducer seed the optimistic
    // temp reply's `attachments[]` without re-resolving against the pool.
    attachments?: CoachingNoteAttachmentChip[];
    coachingFileIds?: Array<CoachingFile["id"]>;
    // Same purpose as `createCoachingNote.mentions` — seeds the temp reply.
    mentions: EntityMention[];
    // Files dropped into the reply composer that aren't yet in the rep's pool.
    // The saga uploads them via createCoachingFile and folds the resulting
    // CoachingFile ids into coachingFileIds, mirroring the note create path.
    newFiles?: Array<Blob | File>;
    note: string;
    parentNoteId: CoachingNote["id"];
    // Mirrors parent's visibility for the optimistic temp row. Server forces the
    // reply's stored visibility to match the parent regardless of what we send.
    parentVisibility: CoachingNoteVisibility;
    subjectUserId: User["id"];
    tempAuthor: Pick<User, "fullName" | "id" | "username">;
    tempCreatedAt: string;
    tempId: CoachingNote["id"];
  },
  { note: CoachingNote; tempId: CoachingNote["id"] },
  { parentNoteId: CoachingNote["id"]; tempId: CoachingNote["id"] }
>();

export const setCategoryFilter = createAction("@dashboard/coaching/notes/setCategoryFilter")<
  number | undefined
>();

export interface ShareInChatPayload {
  // Field Insights charts the selection came from; seed chart-source pills.
  chartIds?: number[];
  entities: PendingShareEntity[];
  mentions?: EntityMention[];
  note?: string;
  // The rep whose composer should receive the share. Unset means whichever rep is active.
  targetUserId?: User["id"];
}

export const shareInChat = createAction(
  "@dashboard/coaching/notes/shareInChat"
)<ShareInChatPayload>();

export const clearPendingShare = createAction("@dashboard/coaching/notes/clearPendingShare")();

// Bumps a counter in state so the panel's existing useEffect re-runs the fetch
// with its current (component-local) filter / search / category — the notifications
// polling saga fires this whenever a "coaching note created" / "coaching note replied"
// row lands so the feed refreshes without manual reload.
export const bumpNotesRefreshCounter = createAction(
  "@dashboard/coaching/notes/bumpRefreshCounter"
)();

export type CoachingNotesActions = ActionType<
  | typeof bumpNotesRefreshCounter
  | typeof clearPendingShare
  | typeof createCoachingNote
  | typeof createReply
  | typeof deleteCoachingNote
  | typeof fetchCoachingNotes
  | typeof fetchReplies
  | typeof setCategoryFilter
  | typeof shareInChat
  | typeof updateCoachingNote
>;
